import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { segment } from "../helpers/segment";

// Provide a very small in-memory shim for environments (SSR/tests) without localStorage.
const safeStorage:
  | Storage
  | {
      getItem: (k: string) => string | undefined;
      setItem: (k: string, v: string) => void;
      removeItem: (k: string) => void;
    } =
  globalThis.window !== undefined && globalThis.localStorage
    ? globalThis.localStorage
    : (() => {
        const store = new Map<string, string>();
        return {
          getItem: (k: string) => (store.has(k) ? store.get(k)! : undefined),
          setItem: (k: string, v: string) => {
            store.set(k, v);
          },
          removeItem: (k: string) => {
            store.delete(k);
          },
        };
      })();

export type Context = {
  rootBusinessId: string;
  rootRole: string;
  filteredBusinessId?: string;
  fullString: string;
};
/**
 * Do not import this, use the `fullContext` atom instead. This is only exported so a hook can do a hard "reset" of the stored state.
 * This is the context id, composed from root business ID, role string, and filtered business ID, i.e. "businessId~role~businessId".
 */
export const contextStringPersisted = atomWithStorage<string | undefined>(
  "latestRootContext",
  undefined,
  {
    getItem(key) {
      return safeStorage.getItem(key) ?? undefined;
    },
    setItem(key, value) {
      safeStorage.setItem(key, value ?? "");
    },
    removeItem(key) {
      safeStorage.removeItem(key);
    },
    //should not subscribe to changes
  },
  {
    getOnInit: true,
  },
);

/**
 * This is the main state that controls the user's current selected context ID, this should be the single source of ultimate truth for reading and writing this state. But, we prefer you to use the hook, useOperatingContext to actually write changes from your components.
 */
export const fullContext = atom(
  (get) => {
    const x = get(contextStringPersisted);
    if (x) {
      const parts = x.split("~");
      if (parts?.length === 3) {
        return {
          rootBusinessId: parts[0],
          rootRole: parts[1],
          filteredBusinessId: parts[2],
          fullString: x,
        };
      }
    }
  },
  (get, set, value: string) => {
    const prevValue = get(contextStringPersisted);
    const parts = value.split("~");

    if (parts?.length === 3) {
      set(contextStringPersisted, value);

      // Track segment event only when root business ID (first part) changes
      if (prevValue) {
        const prevParts = prevValue.split("~");
        const prevRootId = prevParts[0];
        const newRootId = parts[0];

        if (prevRootId !== newRootId) {
          void segment.track("change root context selection", {
            newContextId: value,
            previousContextId: prevValue,
            newRootBusinessId: newRootId,
            previousRootBusinessId: prevRootId,
          });
        }
      } else if (value) {
        // First time setting context (no previous value)
        void segment.track("change root context selection", {
          newContextId: value,
          previousContextId: undefined,
          newRootBusinessId: parts[0],
          previousRootBusinessId: undefined,
        });
      }
    }
  },
);

export const fullContextString = atom((get) => get(contextStringPersisted));
